import React, { createContext, useContext, useState, useEffect } from 'react';
import { TodoItem, TodoContextType } from '../types/todo';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

const TodoContext = createContext<TodoContextType | undefined>(undefined);

export function TodoProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [todoItems, setTodoItems] = useState<TodoItem[]>([]);

  // Load todo items from localStorage when user logs in
  useEffect(() => {
    if (user) {
      const savedTodos = localStorage.getItem(`todo_items_${user.uid}`);
      if (savedTodos) {
        setTodoItems(JSON.parse(savedTodos));
      }
    } else {
      setTodoItems([]);
    }
  }, [user]);

  // Save todo items to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(`todo_items_${user.uid}`, JSON.stringify(todoItems));
    }
  }, [todoItems, user]);

  const addTodoItem = (item: Omit<TodoItem, 'id' | 'createdAt'>) => {
    if (!user) {
      toast.error('Please log in to add todo items');
      return;
    }

    const newItem: TodoItem = {
      ...item,
      id: Date.now().toString() + Math.random().toString(36).substring(2),
      createdAt: new Date().toISOString()
    };

    setTodoItems(prev => [...prev, newItem]);
    toast.success('Todo item added successfully');
  };

  const removeTodoItem = (id: string) => {
    setTodoItems(prev => prev.filter(item => item.id !== id));
    toast.success('Todo item removed');
  };

  const updateTodoItem = (id: string, updates: Partial<TodoItem>) => {
    setTodoItems(prev => prev.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
    toast.success('Todo item updated');
  };

  const getTodosByType = (type: 'fund' | 'stock') => {
    return todoItems.filter(item => item.type === type);
  };

  const getTodosByAction = (action: 'add' | 'exit') => {
    return todoItems.filter(item => item.action === action);
  };

  return (
    <TodoContext.Provider value={{
      todoItems,
      addTodoItem,
      removeTodoItem,
      updateTodoItem,
      getTodosByType,
      getTodosByAction
    }}>
      {children}
    </TodoContext.Provider>
  );
}

export function useTodo() {
  const context = useContext(TodoContext);
  if (!context) {
    throw new Error('useTodo must be used within a TodoProvider');
  }
  return context;
}